import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { handleGetRequest } from '../../service/GetTemplate';
import { endPoints } from '../../Config';
import { useDispatch } from 'react-redux';
import AddEditMachine from './AddEditMachine';
import AssignRouteDialog from './AssignRouteDialog';
import { catalogRouteOptions, machineRouteId } from './machineRouteOptions';
import MachineWarehouseDialog, { canManageWarehouse } from './MachineWarehouseDialog';
import ParlevelComparisonDialog from './ParlevelComparisonDialog';
import { renderToStaticMarkup } from 'react-dom/server';
import MachineQrLabel from './MachineQrLabel';
import { waitForQrFonts } from './machineQrFonts';
import { handleDeleteRequest } from '../../service/DeleteTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';
import { exportToExcel } from '../../utilities/exportToExcel';

const createDefaultTableFilters = () => ({
    assignedTo: null,
    type: [],
    route: [],
    catalogRoute: [],
    model: null,
    location: null,
    machineName: null,
    serialNumber: '',
    deviceId: '',
    externalSystemId: '',
    uuid: '',
    stockStatus: null,
    recordStatus: 'active',
    externalIdStatus: null,
    updatedFrom: null,
    updatedTo: null
});

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const buildOptions = (machines, getter) => {
    const unique = Array.from(
        new Set(
            machines
                .map(getter)
                .map((value) => String(value || '').trim())
                .filter(Boolean)
        )
    );

    return unique
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ label: value, value }));
};

const formatDateLabel = (value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
};

const Machines = () => {
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [warehouseMachineId, setWarehouseMachineId] = useState(null);
    const [warehouseReservations, setWarehouseReservations] = useState([]);
    const [showAssignRoute, setShowAssignRoute] = useState(false);
    const [showParlevelComparison, setShowParlevelComparison] = useState(false);
    const [rowRecord, setRowRecord] = useState(null);
    const [allMachines, setAllMachines] = useState([]);
    const [editable, setEditable] = useState(false);
    const [viewable, setViewable] = useState(false);
    const [displayBasic, setDisplayBasic] = useState(false);
    const [rowId, setRowId] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [tableFilters, setTableFilters] = useState(createDefaultTableFilters);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [datePreset, setDatePreset] = useState(null);

    const dispatch = useDispatch();
    const location = useLocation();

    const currentUser = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const currentRoleUid = currentAdmin?.role?.uid;
    const warehouseAllowed = canManageWarehouse(currentAdmin);
    const t = (value) => translateUiText(value, preferredLanguage);

    const handleAdd = () => {
        setShowAddDialog(true);
        setEditable(false);
        setViewable(false);
    };

    const handleEdit = (rowData) => {
        setShowAddDialog(true);
        setEditable(true);
        setViewable(false);
        setRowRecord(rowData);
    };

    const handleView = (rowData) => {
        setShowAddDialog(true);
        setEditable(false);
        setViewable(true);
        setRowRecord(rowData);
    };

    const onHide = () => {
        setShowAddDialog(false);
        getMachineList();
    };

    const onClose = () => {
        setShowAddDialog(false);
    };

    const handleDisable = (rowData) => {
        const machineIdentifier = rowData?.serialNumber || rowData?.deviceId || rowData?.uuid || t('this machine');
        confirmDialog({
            message: `${t('Do you want to disable')} "${machineIdentifier}"? ${t('The machine record and its history will be kept. It will no longer appear in the active list. You can reactivate it if no other active machine has the same serial number, device ID or external ID.')}`,
            header: t('Disable machine'),
            acceptLabel: t('Disable'),
            rejectLabel: t('Cancel'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                const response = await dispatch(handleDeleteRequest(endPoints.deleteMachine(rowData._id), true, false));
                if (response?.status === 200) {
                    toast.success(t('Machine disabled. Its record and history have been preserved.'), { autoClose: 3000 });
                    getMachineList();
                }
            },
            reject: () => {}
        });
    };

    const handleReactivate = (rowData) => {
        const machineIdentifier = rowData?.serialNumber || rowData?.deviceId || rowData?.uuid || t('this machine');
        confirmDialog({
            message: `${t('Are you sure you want to reactivate')} "${machineIdentifier}"?`,
            header: t('Confirm Reactivation'),
            icon: 'pi pi-refresh',
            accept: async () => {
                const response = await dispatch(handlePutRequest({}, endPoints.reactivateMachine(rowData._id), true, true));
                if (response?.status === 200) {
                    getMachineList();
                }
            },
            reject: () => {}
        });
    };

    const loadWarehouseReservations = useCallback(async () => {
        if (!warehouseAllowed) return;
        const response = await handleGetRequest(endPoints.machineWarehouseReservations, false, false, dispatch);
        if (Array.isArray(response?.data)) setWarehouseReservations(response.data);
    }, [dispatch, warehouseAllowed]);
    useEffect(() => { loadWarehouseReservations(); }, [loadWarehouseReservations]);

    const actionBodyTemplate = (rowData) => {
        if (rowData?.isDeleted === true) {
            return (
                <div className="actions">
                    <Button
                        tooltip={t('Reactivate')}
                        label={t('Reactivate')}
                        icon="pi pi-refresh"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => {
                            handleReactivate(rowData);
                        }}
                        className="p-button-rounded p-button-success p-button-sm"
                    />
                </div>
            );
        }

        return (
            <div className="actions">
                {warehouseAllowed && <Button label="Bodega" icon="pi pi-box" onClick={() => setWarehouseMachineId(rowData._id)} className="p-button-sm mr-2" />}
                <Button
                    tooltip={t('Edit')}
                    icon="pi pi-pencil"
                    tooltipOptions={{ position: 'top' }}
                    disabled={['Pending', 'Rejected'].includes(rowData?.statusName) || (rowData?.statusName === 'Approved' && rowData?.status === 'N')}
                    onClick={() => {
                        handleEdit(rowData);
                    }}
                    className="p-button-rounded p-button-primary mr-2 ml-2"
                />

                <Button
                    tooltip={t('View')}
                    tooltipOptions={{ position: 'top' }}
                    icon="pi pi-eye"
                    onClick={() => {
                        handleView(rowData);
                    }}
                    className="p-button-rounded mr-2"
                    style={{ backgroundColor: '#F2AC57', border: 'none' }}
                />
                <Button
                    tooltip={t('Disable machine')}
                    aria-label={t('Disable machine')}
                    icon="pi pi-ban"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => {
                        handleDisable(rowData);
                    }}
                    className="p-button-rounded p-button-danger"
                />
            </div>
        );
    };

    const getAssignedToLabel = (rowData) => {
        const fullName = `${rowData?.userFirstName || ''} ${rowData?.userLastName || ''}`.trim();
        return fullName || t('Unassigned');
    };

    const assignedToOptions = useMemo(() => buildOptions(allMachines, (machine) => getAssignedToLabel(machine)), [allMachines]);
    const typeOptions = useMemo(() => buildOptions(allMachines, (machine) => machine?.type), [allMachines]);
    const databaseRouteOptions = useMemo(() => catalogRouteOptions(allMachines), [allMachines]);
    const routeOptions = useMemo(() => buildOptions(allMachines, (machine) => machine?.legacyRoute ?? machine?.route), [allMachines]);
    const modelOptions = useMemo(() => buildOptions(allMachines, (machine) => machine?.model), [allMachines]);
    const locationOptions = useMemo(() => buildOptions(allMachines, (machine) => machine?.location), [allMachines]);
    const machineOptions = useMemo(() => buildOptions(allMachines, (machine) => machine?.machine), [allMachines]);

    const stockStatusOptions = [
        { label: 'Fuera de inventario', value: 'installed' },
        { label: t('In Stock'), value: 'inStock' }
    ];

    const recordStatusOptions = [
        { label: t('Active'), value: 'active' },
        { label: t('Disabled'), value: 'deleted' }
    ];

    const externalIdStatusOptions = [
        { label: t('Has External ID'), value: 'synced' },
        { label: t('No External ID'), value: 'notSynced' }
    ];

    const filteredMachines = useMemo(() => {
        const keyword = normalizeText(globalFilter);

        return allMachines.filter((machine) => {
            const assignedTo = getAssignedToLabel(machine);
            const route = String(machine?.legacyRoute ?? machine?.route ?? '').trim();
            const type = String(machine?.type || '').trim();
            const model = String(machine?.model || '').trim();
            const location = String(machine?.location || '').trim();
            const machineName = String(machine?.machine || '').trim();
            const serialNumber = String(machine?.serialNumber || '').trim();
            const deviceId = String(machine?.deviceId || '').trim();
            const externalSystemId = String(machine?.externalSystemId || '').trim();
            const uuid = String(machine?.uuid || '').trim();

            if (keyword) {
                const searchableFields = [
                    machineName,
                    location,
                    route,
                    machine.catalogRouteName,
                    model,
                    serialNumber,
                    deviceId,
                    externalSystemId,
                    uuid,
                    assignedTo
                ];
                const matchesKeyword = searchableFields.some((field) => normalizeText(field).includes(keyword));
                if (!matchesKeyword) return false;
            }

            if (tableFilters.serialNumber && normalizeText(serialNumber) !== normalizeText(tableFilters.serialNumber)) return false;
            if (tableFilters.deviceId && normalizeText(deviceId) !== normalizeText(tableFilters.deviceId)) return false;
            if (tableFilters.externalSystemId && normalizeText(externalSystemId) !== normalizeText(tableFilters.externalSystemId)) return false;
            if (tableFilters.uuid && normalizeText(uuid) !== normalizeText(tableFilters.uuid)) return false;

            if (tableFilters.assignedTo && assignedTo !== tableFilters.assignedTo) return false;
            if (tableFilters.type?.length && !tableFilters.type.includes(type)) return false;
            if (tableFilters.catalogRoute?.length && !tableFilters.catalogRoute.includes(machineRouteId(machine))) return false;
            if (tableFilters.route?.length && !tableFilters.route.includes(route)) return false;
            if (tableFilters.model && model !== tableFilters.model) return false;
            if (tableFilters.location && location !== tableFilters.location) return false;
            if (tableFilters.machineName && machineName !== tableFilters.machineName) return false;

            if (tableFilters.stockStatus === 'inStock' && machine?.inStockInventory !== true) return false;
            if (tableFilters.stockStatus === 'installed' && machine?.inStockInventory === true) return false;

            if (tableFilters.recordStatus === 'active' && machine?.isDeleted === true) return false;
            if (tableFilters.recordStatus === 'deleted' && machine?.isDeleted !== true) return false;

            const hasExternalId = externalSystemId !== '';
            if (tableFilters.externalIdStatus === 'synced' && !hasExternalId) return false;
            if (tableFilters.externalIdStatus === 'notSynced' && hasExternalId) return false;

            if (tableFilters.updatedFrom || tableFilters.updatedTo) {
                const updatedAt = machine?.updatedAt ? new Date(machine.updatedAt) : null;
                if (!updatedAt || Number.isNaN(updatedAt.getTime())) return false;

                if (tableFilters.updatedFrom) {
                    const from = new Date(tableFilters.updatedFrom);
                    from.setHours(0, 0, 0, 0);
                    if (updatedAt < from) return false;
                }

                if (tableFilters.updatedTo) {
                    const to = new Date(tableFilters.updatedTo);
                    to.setHours(23, 59, 59, 999);
                    if (updatedAt > to) return false;
                }
            }

            return true;
        });
    }, [allMachines, globalFilter, tableFilters]);

    const activeFilterChips = useMemo(() => {
        const chips = [];

        if (globalFilter) chips.push({ key: 'globalFilter', label: t('Search'), value: globalFilter });
        if (tableFilters.serialNumber) chips.push({ key: 'serialNumber', label: t('Serial'), value: tableFilters.serialNumber });
        if (tableFilters.deviceId) chips.push({ key: 'deviceId', label: t('Device ID'), value: tableFilters.deviceId });
        if (tableFilters.externalSystemId) chips.push({ key: 'externalSystemId', label: t('External ID'), value: tableFilters.externalSystemId });
        if (tableFilters.uuid) chips.push({ key: 'uuid', label: 'UUID', value: tableFilters.uuid });
        if (tableFilters.assignedTo) chips.push({ key: 'assignedTo', label: t('Assigned To'), value: tableFilters.assignedTo });
        (tableFilters.catalogRoute || []).forEach(value => chips.push({ key: 'catalogRoute', label: preferredLanguage === 'en' ? 'Database route' : 'Ruta de la BD', value, displayValue: databaseRouteOptions.find(option => option.value === value)?.label || value, isArray: true }));
        (tableFilters.route || []).forEach((value) => chips.push({ key: 'route', label: preferredLanguage === 'en' ? 'Legacy route' : 'Ruta heredada', value, isArray: true }));
        (tableFilters.type || []).forEach((value) => chips.push({ key: 'type', label: t('Type'), value, isArray: true }));
        if (tableFilters.model) chips.push({ key: 'model', label: t('Model'), value: tableFilters.model });
        if (tableFilters.location) chips.push({ key: 'location', label: t('Location'), value: tableFilters.location });
        if (tableFilters.machineName) chips.push({ key: 'machineName', label: t('Machine'), value: tableFilters.machineName });
        if (tableFilters.stockStatus) chips.push({ key: 'stockStatus', label: t('Stock Status'), value: (tableFilters.stockStatus === 'inStock' ? t('In Stock') : 'Fuera de inventario') });
        if (tableFilters.recordStatus) chips.push({ key: 'recordStatus', label: t('Record Status'), value: t(tableFilters.recordStatus === 'active' ? 'Active' : 'Disabled') });
        if (tableFilters.externalIdStatus) chips.push({ key: 'externalIdStatus', label: t('External ID Status'), value: t(tableFilters.externalIdStatus === 'synced' ? 'Has External ID' : 'No External ID') });
        if (tableFilters.updatedFrom) chips.push({ key: 'updatedFrom', label: t('Updated From'), value: formatDateLabel(tableFilters.updatedFrom) });
        if (tableFilters.updatedTo) chips.push({ key: 'updatedTo', label: t('Updated To'), value: formatDateLabel(tableFilters.updatedTo) });

        return chips;
    }, [globalFilter, tableFilters, preferredLanguage, databaseRouteOptions]);

    const clearChip = (chip) => {
        if (chip.key === 'globalFilter') {
            setGlobalFilter('');
            return;
        }

        setTableFilters((prev) => {
            const next = { ...prev };

            if (chip.isArray) {
                next[chip.key] = (prev[chip.key] || []).filter((value) => value !== chip.value);
                return next;
            }

            next[chip.key] = typeof prev[chip.key] === 'string' ? '' : null;
            return next;
        });
    };

    const generateQR = (rowData) => {
        return (
            <Button
                onClick={() => {
                    setRowId(rowData);
                    setDisplayBasic(true);
                }}
                tooltip={t('Generate QR code for this machine')}
                tooltipOptions={{ position: 'top' }}
            >
                {t('Generate QR')}
            </Button>
        );
    };

    const renderFooter = () => {
        return (
            <div className="flex justify-content-center my-3">
                <Button
                    label={t('Print QR')}
                    className="Btn__Dark w-9rem"
                    tooltip={t('Print this QR code')}
                    tooltipOptions={{ position: 'top' }}
                    onClick={printQR}
                />
            </div>
        );
    };

    const printQR = () => {
        if (!rowId?.uuid) return;
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            window.alert(preferredLanguage === 'en'
                ? 'Allow pop-up windows to print the label.'
                : 'Permite las ventanas emergentes para imprimir la etiqueta.');
            return;
        }
        const label = renderToStaticMarkup(<MachineQrLabel machine={rowId} />);
        printWindow.document.write(`<!doctype html><html lang="es"><head>
            <meta charset="utf-8"><title>Etiqueta QR</title>
            <style>
                @page { size: auto; margin: 10mm; }
                * { box-sizing: border-box; }
                body { margin: 0; background: white; }
                .label { width: 140mm; max-width: 100%; break-inside: avoid;
                    print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                .label svg { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            </style></head><body><div class="label">${label}</div></body></html>`);
        printWindow.onload = async () => {
            try {
                await waitForQrFonts(printWindow.document);
                if (printWindow.closed) return;
                printWindow.focus();
                printWindow.print();
            } catch {
                if (!printWindow.closed) printWindow.close();
                window.alert(preferredLanguage === 'en'
                    ? 'The label fonts could not load. Please try printing again.'
                    : 'No se pudieron cargar las fuentes de la etiqueta. Vuelve a intentar imprimir.');
            }
        };
        printWindow.document.close();
    };

    const getMachineList = useCallback(async () => {
        const canIncludeDeleted = ['admin', 'superAdmin', 'support'].includes(currentRoleUid);
        const machineEndpoint = canIncludeDeleted ? `${endPoints.getallMachine}?includeDeleted=true` : endPoints.getallMachine;
        const response = await handleGetRequest(machineEndpoint, true, false, dispatch);
        if (response) {
            setAllMachines(response?.data?.map(machine => ({
                ...machine,
                legacyRoute: machine.route, legacyLocation: machine.location,
                route: machine.catalogRouteName || machine.route,
                location: machine.catalogLocationName || machine.location
            })));
        }
    }, [currentRoleUid, dispatch]);

    const resetFilters = () => {
        setGlobalFilter('');
        setTableFilters(createDefaultTableFilters());
        setDatePreset(null);
    };

    const exportMachines = () => {
        const exportRows = filteredMachines.map((machine) => ({
            model: machine?.model || '-',
            type: translateUiText(machine?.type || '-', preferredLanguage),
            location: machine?.location || '-',
            route: machine?.route || '-',
            machine: machine?.machine || '-',
            serialNumber: machine?.serialNumber || '-',
            deviceId: machine?.deviceId || '-',
            externalSystemId: machine?.externalSystemId || '-',
            uuid: machine?.uuid || '-',
            assignedTo: getAssignedToLabel(machine),
            stockStatus: machine?.inStockInventory === true ? t('In Stock') : 'Fuera de inventario',
            recordStatus: machine?.isDeleted ? t('Disabled') : t('Active'),
            updatedAt: machine?.updatedAt ? formatDateLabel(machine.updatedAt) : '-'
        }));

        const columns = [
            { key: 'model', header: t('Model') },
            { key: 'type', header: t('Type') },
            { key: 'location', header: t('Location') },
            { key: 'route', header: t('Route') },
            { key: 'machine', header: t('Machine') },
            { key: 'serialNumber', header: t('Serial Number') },
            { key: 'deviceId', header: t('Device ID') },
            { key: 'externalSystemId', header: t('External ID') },
            { key: 'uuid', header: 'UUID' },
            { key: 'assignedTo', header: t('Assigned To') },
            { key: 'stockStatus', header: t('In Stock') },
            { key: 'recordStatus', header: t('Record Status') },
            { key: 'updatedAt', header: t('Updated To') }
        ];

        const stamp = new Date().toISOString().slice(0, 10);
        exportToExcel(exportRows, `Machines_${stamp}`, columns);
    };

    const applyDatePreset = (preset) => {
        const now = new Date();
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        const endOfToday = new Date(now);
        endOfToday.setHours(23, 59, 59, 999);

        if (preset === 'today') {
            setTableFilters((prev) => ({ ...prev, updatedFrom: startOfToday, updatedTo: endOfToday }));
            setDatePreset('today');
            return;
        }

        if (preset === '7d' || preset === '30d') {
            const days = preset === '7d' ? 7 : 30;
            const from = new Date(now);
            from.setDate(from.getDate() - (days - 1));
            from.setHours(0, 0, 0, 0);
            setTableFilters((prev) => ({ ...prev, updatedFrom: from, updatedTo: endOfToday }));
            setDatePreset(preset);
            return;
        }

        setDatePreset('custom');
    };

    useEffect(() => {
        getMachineList();
    }, [getMachineList]);

    useEffect(() => {
        const syncPreferredLanguage = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', syncPreferredLanguage);
        window.addEventListener('storage', syncPreferredLanguage);

        return () => {
            window.removeEventListener('preferred-language-changed', syncPreferredLanguage);
            window.removeEventListener('storage', syncPreferredLanguage);
        };
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const searchValue = searchParams.get('search') || '';
        setGlobalFilter(searchValue);
    }, [location.search]);

    return (
        <div className="p-4">
            <ConfirmDialog />
            {warehouseMachineId && <MachineWarehouseDialog machineId={warehouseMachineId} onClose={() => setWarehouseMachineId(null)} onSaved={() => { setWarehouseMachineId(null); getMachineList(); loadWarehouseReservations(); }} />}
            {showParlevelComparison && ['admin', 'superAdmin', 'support'].includes(currentRoleUid) && <ParlevelComparisonDialog initialRoute={tableFilters.route?.length === 1 ? tableFilters.route[0] : 'UTP'} onClose={() => setShowParlevelComparison(false)} onImported={getMachineList} />}
            {showAssignRoute && ['admin', 'superAdmin'].includes(currentRoleUid) && <AssignRouteDialog
                machines={allMachines}
                initialRoute={tableFilters.route?.length === 1 ? tableFilters.route[0] : null}
                initialRouteId={tableFilters.catalogRoute?.length === 1 ? tableFilters.catalogRoute[0] : null}
                preferredLanguage={preferredLanguage}
                onClose={() => setShowAssignRoute(false)}
                onAssigned={() => { setShowAssignRoute(false); getMachineList(); }}
            />}

            <Dialog
                header={t('QR Code')}
                visible={displayBasic}
                style={{ width: '650px', maxWidth: '95vw' }}
                footer={renderFooter()}
                onHide={() => {
                    setDisplayBasic(false);
                }}
            >
                <div id="qr" className="flex justify-content-center mt-3">
                    <MachineQrLabel machine={rowId} />
                </div>
            </Dialog>

            <Dialog
                header={<div>{editable ? t('Edit Machine') : viewable ? t('View Machine') : t('Add Machine')}</div>}
                visible={showAddDialog}
                onHide={onClose}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '40vw' }}
                draggable={false}
            >
                <AddEditMachine rowRecord={rowRecord} editable={editable} viewable={viewable} onHide={onHide} onClose={onClose} preferredLanguage={preferredLanguage} />
            </Dialog>

            <div className="flex justify-content-between align-items-center mb-3">
                <h3>{t('Machines')}</h3>

                <div className="flex gap-2 align-items-center">
                    {['admin', 'superAdmin', 'support'].includes(currentRoleUid) && <Button label="Comparar con Parlevel" icon="pi pi-search" onClick={() => setShowParlevelComparison(true)} />}
                    {['admin', 'superAdmin'].includes(currentRoleUid) && <Button
                        label={preferredLanguage === 'en' ? 'Assign by route' : 'Asignar por ruta'}
                        icon="pi pi-users" onClick={() => setShowAssignRoute(true)}
                    />}
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder={t('Search machine, location, route, model, serial, device ID, external ID, UUID, operator')}
                            className="p-inputtext-sm"
                            style={{ minWidth: '24rem' }}
                        />
                    </span>
                    <Button
                        label={t('Export to Excel')}
                        icon="pi pi-file-excel"
                        className="p-button-sm p-button-outlined"
                        onClick={exportMachines}
                        tooltip={t('Export visible machine list to Excel')}
                        tooltipOptions={{ position: 'top' }}
                    />
                    <Button
                        label={t('Add New')}
                        icon="pi pi-plus"
                        className="p-button-success p-button-sm"
                        tooltip={t('Add a new machine')}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleAdd()}
                    />
                </div>
            </div>

            <div className="card mb-3">
                <div className="flex justify-content-between align-items-center mb-3">
                    <div>
                        <div className="text-lg font-semibold">{filteredMachines.length} {t('machines')}</div>
                        <small className="text-600">{t('Filters update the list instantly. Search supports serial, device ID, external ID, UUID, route, machine, and operator.')}</small>
                    </div>
                    <Button
                        label={t('Clear Filters')}
                        icon="pi pi-filter-slash"
                        className="p-button-text p-button-sm"
                        onClick={resetFilters}
                    />
                </div>

                {activeFilterChips.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {activeFilterChips.map((chip, index) => (
                            <div key={`${chip.key}-${chip.value}-${index}`} className="flex align-items-center gap-2 px-2 py-1 surface-100 border-round">
                                <span className="text-sm">
                                    <strong>{chip.label}:</strong> {chip.displayValue || chip.value}
                                </span>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-rounded p-button-sm"
                                    onClick={() => clearChip(chip)}
                                    aria-label={`Remove ${chip.label} filter`}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid">
                    <div className="col-12">
                        <div className="text-700 font-semibold mb-2">{t('Quick Filters')}</div>
                    </div>
                    <div className="col-12">
                        <small className="text-600">{t('Use quick filters for daily work. Open advanced filters for IDs, assignment, and date details.')}</small>
                    </div>
                    <div className="col-12 md:col-3">
                        <label htmlFor="machine-database-route" className="block mb-2">{preferredLanguage === 'en' ? 'Database route' : 'Ruta de la BD'}</label>
                        <MultiSelect inputId="machine-database-route" value={tableFilters.catalogRoute} options={databaseRouteOptions}
                            onChange={e => setTableFilters(prev => ({ ...prev, catalogRoute: e.value || [] }))}
                            placeholder={t('All routes')} className="w-full" display="chip" filter showClear maxSelectedLabels={3} />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{preferredLanguage === 'en' ? 'Legacy route' : 'Ruta heredada'}</label>
                        <MultiSelect
                            value={tableFilters.route}
                            options={routeOptions}
                            onChange={(e) => setTableFilters((prev) => ({ ...prev, route: e.value || [] }))}
                            placeholder={t('All routes')}
                            className="w-full"
                            display="chip"
                            filter
                            showClear
                            maxSelectedLabels={3}
                            selectedItemsLabel={preferredLanguage === 'es' ? '{0} rutas seleccionadas' : '{0} routes selected'}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Type')}</label>
                        <MultiSelect
                            value={tableFilters.type}
                            options={typeOptions}
                            onChange={(e) => setTableFilters((prev) => ({ ...prev, type: e.value || [] }))}
                            placeholder={t('All types')}
                            className="w-full"
                            display="chip"
                            filter
                            showClear
                            maxSelectedLabels={3}
                            selectedItemsLabel={preferredLanguage === 'es' ? '{0} tipos seleccionados' : '{0} types selected'}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Stock Status')}</label>
                        <Dropdown
                            value={tableFilters.stockStatus}
                            options={stockStatusOptions}
                            onChange={(e) => setTableFilters((prev) => ({ ...prev, stockStatus: e.value }))}
                            placeholder="Todas las máquinas"
                            className="w-full"
                            showClear
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Record Status')}</label>
                        <Dropdown
                            value={tableFilters.recordStatus}
                            options={recordStatusOptions}
                            onChange={(e) => setTableFilters((prev) => ({ ...prev, recordStatus: e.value }))}
                            placeholder={t('Active')}
                            className="w-full"
                            showClear
                        />
                    </div>
                    <div className="col-12 md:col-3 flex align-items-end">
                        <Button
                            label={showAdvancedFilters ? t('Hide Advanced Filters') : t('Show Advanced Filters')}
                            icon={showAdvancedFilters ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            className="p-button-outlined p-button-sm w-full"
                            onClick={() => setShowAdvancedFilters((prev) => !prev)}
                        />
                    </div>

                    {showAdvancedFilters && (
                        <>
                            <div className="col-12">
                                <div className="text-700 font-semibold mb-2 mt-2">{t('Identity')}</div>
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('Serial Number')}</label>
                                <InputText
                                    value={tableFilters.serialNumber}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, serialNumber: e.target.value }))}
                                    placeholder={t('Exact serial')}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('Device ID')}</label>
                                <InputText
                                    value={tableFilters.deviceId}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, deviceId: e.target.value }))}
                                    placeholder={t('Exact device ID')}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('External ID')}</label>
                                <InputText
                                    value={tableFilters.externalSystemId}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, externalSystemId: e.target.value }))}
                                    placeholder={t('Exact external ID')}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">UUID</label>
                                <InputText
                                    value={tableFilters.uuid}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, uuid: e.target.value }))}
                                    placeholder={t('Exact UUID')}
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12">
                                <div className="text-700 font-semibold mb-2 mt-2">{t('Assignment')}</div>
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('Assigned To')}</label>
                                <Dropdown
                                    value={tableFilters.assignedTo}
                                    options={assignedToOptions}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, assignedTo: e.value }))}
                                    placeholder={t('All operators')}
                                    className="w-full"
                                    showClear
                                    filter
                                />
                            </div>

                            <div className="col-12">
                                <div className="text-700 font-semibold mb-2 mt-2">{t('Classification')}</div>
                            </div>
                            <div className="col-12 md:col-2">
                                <label className="block mb-2">{t('Model')}</label>
                                <Dropdown
                                    value={tableFilters.model}
                                    options={modelOptions}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, model: e.value }))}
                                    placeholder={t('All models')}
                                    className="w-full"
                                    showClear
                                    filter
                                />
                            </div>
                            <div className="col-12 md:col-2">
                                <label className="block mb-2">{t('Location')}</label>
                                <Dropdown
                                    value={tableFilters.location}
                                    options={locationOptions}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, location: e.value }))}
                                    placeholder={t('All locations')}
                                    className="w-full"
                                    showClear
                                    filter
                                />
                            </div>
                            <div className="col-12 md:col-2">
                                <label className="block mb-2">{t('Machine')}</label>
                                <Dropdown
                                    value={tableFilters.machineName}
                                    options={machineOptions}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, machineName: e.value }))}
                                    placeholder={t('All machines')}
                                    className="w-full"
                                    showClear
                                    filter
                                />
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('External ID Status')}</label>
                                <Dropdown
                                    value={tableFilters.externalIdStatus}
                                    options={externalIdStatusOptions}
                                    onChange={(e) => setTableFilters((prev) => ({ ...prev, externalIdStatus: e.value }))}
                                    placeholder={t('Has + No External ID')}
                                    className="w-full"
                                    showClear
                                />
                            </div>

                            <div className="col-12">
                                <div className="text-700 font-semibold mb-2 mt-2">{t('Dates')}</div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="flex gap-2 flex-wrap">
                                    <Button label={t('Today')} className={`p-button-sm ${datePreset === 'today' ? '' : 'p-button-outlined'}`} onClick={() => applyDatePreset('today')} />
                                    <Button label={t('Last 7 Days')} className={`p-button-sm ${datePreset === '7d' ? '' : 'p-button-outlined'}`} onClick={() => applyDatePreset('7d')} />
                                    <Button label={t('Last 30 Days')} className={`p-button-sm ${datePreset === '30d' ? '' : 'p-button-outlined'}`} onClick={() => applyDatePreset('30d')} />
                                    <Button
                                        label={t('Custom')}
                                        className={`p-button-sm ${datePreset === 'custom' ? '' : 'p-button-outlined'}`}
                                        onClick={() => applyDatePreset('custom')}
                                    />
                                </div>
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('Updated From')}</label>
                                <Calendar
                                    value={tableFilters.updatedFrom}
                                    onChange={(e) => {
                                        setTableFilters((prev) => ({ ...prev, updatedFrom: e.value }));
                                        setDatePreset('custom');
                                    }}
                                    dateFormat="yy-mm-dd"
                                    placeholder={t('Start date')}
                                    showIcon
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('Updated To')}</label>
                                <Calendar
                                    value={tableFilters.updatedTo}
                                    onChange={(e) => {
                                        setTableFilters((prev) => ({ ...prev, updatedTo: e.value }));
                                        setDatePreset('custom');
                                    }}
                                    dateFormat="yy-mm-dd"
                                    placeholder={t('End date')}
                                    showIcon
                                    className="w-full"
                                    minDate={tableFilters.updatedFrom || null}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <DataTable
                paginator
                rows={15}
                rowsPerPageOptions={[5, 10, 20]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('Showing {first} to {last} of {totalRecords} Records')}
                emptyMessage={t('No List found.')}
                responsiveLayout="scroll"
                value={filteredMachines}
                className="p-datatable-sm datatable-responsive"
            >
                <Column header={t('Model')} field="model" />
                <Column header={t('Type')} body={(rowData) => translateUiText(rowData.type || '-', preferredLanguage)} field="type" />
                <Column header={t('Location')} field="location" body={row => row.inStockInventory ? (row.warehouseName || 'En bodega') : row.location} />
                <Column header={t('Route')} field="route" body={row => <div>{row.route}<small className="block text-500">{row.routeId ? 'Catálogo maestro' : 'Heredada · pendiente de vincular'}</small></div>} />
                <Column header={t('Machine')} field="machine" />
                <Column header={t('Serial Number')} body={(rowData) => rowData.serialNumber || '-'} field="serialNumber" />
                <Column header={t('Device ID')} body={(rowData) => rowData.deviceId || '-'} field="deviceId" />
                <Column
                    header={t('Assigned To')}
                    body={(rowData) => {
                        return getAssignedToLabel(rowData);
                    }}
                />
                <Column header={t('Generate QR')} body={generateQR} />
                <Column
                    header={t('In Stock')}
                    body={(rowData) => {
                        const inStock = rowData?.inStockInventory === true;
                        return (
                            <div className="flex justify-content-center">
                                <Tag
                                    value={inStock ? (warehouseReservations.some(item => String(item.machine) === String(rowData._id)) ? 'En inventario · Reservada para instalación' : t('In Stock')) : 'Fuera de inventario'}
                                    severity={inStock ? 'success' : 'warning'}
                                    icon={inStock ? 'pi pi-check-circle' : 'pi pi-box'}
                                />
                            </div>
                        );
                    }}
                />

                <Column header={t('Actions')} body={actionBodyTemplate} className="Table__ActionHeading__CentredAlign" />
            </DataTable>
        </div>
    );
};

export default Machines;
